import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, FileJson, AlertTriangle } from 'lucide-react';
import { useSelector, useDispatch, useStore } from 'react-redux';
import { useParams, useLocation } from 'react-router-dom';
import { setSuccessfulExpedientes, setErrorExpedientes, filterDeclarationsByErrors, updateWorkflowSessionState, setOcrInputID } from '../../store/reducers';
import { useLanguage } from '../../hooks/useLanguage';
import collapseIcon from "../../assets/collapse-icon.png";
import '../Sidebar/Sidebar.css';
import { getDeclarationManagementTranslation } from '../../hooks/useLanguage';
import apiFetch from '../../utils/api';
import { ensureTaricJsonBinaryFields } from '../../hooks/useDocumentProcessing';
import { readRowIndex, readDuasDeclarations } from '../../utils/declarationKindUtils';
import closeIcon from "../../assets/close-icon.png";
import checkIcon from "../../assets/check-filled-icon.png";
import { GeneratedSuccessfullyModal } from '../Dashboard/GeneratedSuccessfullyModal.jsx';

// Find a saved declaration entry by id across export and import arrays.
const findGroupEntryById = (group, declarationId) =>
  (group?.exportaciones || []).find(exp => String(exp.id) === String(declarationId))
  || (group?.importaciones || []).find(exp => String(exp.id) === String(declarationId))
  || null;

// Read the entry at a given index from either the export or import array.
const groupEntryAtIndex = (group, index) =>
  group?.exportaciones?.[index] || group?.importaciones?.[index] || null;


export const GenerateTab = ({ 
  isSidebarCollapsed,
  setIsSidebarCollapsed,
  setIsGenerateTabOpen,
  setIsGenerateTabCollapsed,
  isGenerateTabOpen: parentIsGenerateTabOpen
}) => {
  const [showGenerateTab, setShowGenerateTab] = useState(false);
  
  // Sync with parent state when it changes
  useEffect(() => {
    if (parentIsGenerateTabOpen !== undefined) {
      setShowGenerateTab(parentIsGenerateTabOpen);
    }
  }, [parentIsGenerateTabOpen]);

  const [isGenerateTabCollapsed, setLocalGenerateTabCollapsed] = useState(() => {
    const storedValue = localStorage.getItem('isGenerateTabCollapsed');
    return storedValue !== null ? JSON.parse(storedValue) : false;
  });
  const [isSpanish] = useLanguage();
  const location = useLocation();
  const dispatch = useDispatch();
  const store = useStore();
  const { id: urlId } = useParams(); // Get ID from URL when in DeclarationView
  
  // OPTIMIZED: Track stored ocrInputID to prevent unnecessary dispatches
  const storedOcrInputIdRef = useRef(null);
  const storedWorkflowSessionIdRef = useRef(null);
  const activeWorkflowSessionId = useSelector((state) => state.workflowSessions?.activeSessionId);
  
  // OPTIMIZED: Reset refs when workflow session changes (ensures updates work after switching workflows)
  useEffect(() => {
    if (activeWorkflowSessionId !== storedWorkflowSessionIdRef.current) {
      storedOcrInputIdRef.current = null;
      storedWorkflowSessionIdRef.current = activeWorkflowSessionId;
    }
  }, [activeWorkflowSessionId]);
  
  const preparedJson = useSelector((state) => state.preparedJson);
  const groupedTaricJsonOutputs = useSelector((state) => state.groupedTaricJsonOutputs || []);
  const selectedExportacionesIndex = useSelector((state) => state.preparedJson.selectedExportacionesIndex);
  const ocrInputID = useSelector((state) => state.preparedJson.ocrInputID);
  const selectedDeclaration = useSelector((state) => state.selectedDeclaration);
  const [isResending, setIsResending] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [errorDetails, setErrorDetails] = useState([]);
  const [editedJsonString, setEditedJsonString] = useState('');
  const textareaRef = useRef(null);
  const previousLocationRef = useRef(location.pathname);

  // Helper: Find a declaration entry by ID across export and import arrays
  const findExportacionById = useCallback((declarationId) => {
    const id = typeof declarationId === 'string' ? parseInt(declarationId, 10) : declarationId;
    for (const group of groupedTaricJsonOutputs) {
      const entry = (group.exportaciones || []).find(exp => String(exp.id) === String(id))
        || (group.importaciones || []).find(exp => String(exp.id) === String(id));
      if (entry?.taric) return entry.taric;
    }
    return null;
  }, [groupedTaricJsonOutputs]);

  // Helper: Find a declaration by ocrInputID and index (export or import array)
  const findExportacionByOcrAndIndex = useCallback((ocrId, index) => {
    if (!ocrId || index === null || groupedTaricJsonOutputs.length === 0) return null;

    const matchingGroup = groupedTaricJsonOutputs.find(
      group => String(group.ocr_id) === String(ocrId) || group.ocr_id === parseInt(ocrId)
    );

    const entry = matchingGroup?.exportaciones?.[index] || matchingGroup?.importaciones?.[index];
    return entry?.taric || null;
  }, [groupedTaricJsonOutputs]);

  // Get the current JSON string from various sources (simplified priority order)
  const getCurrentJsonString = useCallback(() => {
    // Priority 1: Use preparedJson.taricJson (most reliable - set when Export to Taric is clicked)
    if (preparedJson?.taricJson) {
      return JSON.stringify(preparedJson.taricJson, null, 2);
    }
    
    // Priority 2: Find by URL ID (when in DeclarationView)
    if (urlId) {
      const taricJson = findExportacionById(urlId);
      if (taricJson) return JSON.stringify(taricJson, null, 2);
    }
    
    // Priority 3: Find by ocrInputID and selectedExportacionesIndex
    const taricJsonByOcr = findExportacionByOcrAndIndex(ocrInputID, selectedExportacionesIndex);
    if (taricJsonByOcr) return JSON.stringify(taricJsonByOcr, null, 2);
    
    // Priority 4: Find by selectedDeclaration ID
    if (selectedDeclaration?.id) {
      const taricJson = findExportacionById(selectedDeclaration.id);
      if (taricJson) return JSON.stringify(taricJson, null, 2);
    }
    
    return '{}';
  }, [preparedJson?.taricJson, urlId, ocrInputID, selectedExportacionesIndex, selectedDeclaration?.id, findExportacionById, findExportacionByOcrAndIndex]);

  // Handle route changes - close tab when navigating away from declaration routes
  useEffect(() => {
    const currentPath = location.pathname;
    const isDeclarationRoute = currentPath.startsWith('/declaration/') || 
                               currentPath === '/declaration-management';
    
    // Close tab if navigating away from declaration routes
    if (!isDeclarationRoute && showGenerateTab) {
      setShowGenerateTab(false);
      setIsGenerateTabOpen?.(false);
      setLocalGenerateTabCollapsed(false);
      localStorage.setItem('isGenerateTabCollapsed', JSON.stringify(false));
      setIsGenerateTabCollapsed?.(false);
      return;
    }
    
    // On declaration route - reset errors and update JSON when route changes
    const previousPath = previousLocationRef.current;
    if (isDeclarationRoute && currentPath !== previousPath) {
      setErrorDetails([]);
      setHasError(false);
      setErrorMessage('');
      setEditedJsonString(getCurrentJsonString());
    }
    
    previousLocationRef.current = currentPath;
  }, [location.pathname, showGenerateTab, getCurrentJsonString, setIsGenerateTabOpen, setIsGenerateTabCollapsed]);

  // Update editedJsonString when source JSON changes or when row selection changes
  useEffect(() => {
    const currentJson = getCurrentJsonString();
    setEditedJsonString(currentJson);
  }, [getCurrentJsonString]);

  // Clear validation errors when switching to a different declaration/row
  useEffect(() => {
    // Clear errors when any of these change (indicating a different declaration was selected)
    setErrorDetails([]);
    setHasError(false);
    setErrorMessage('');
  }, [preparedJson?.taricJson, ocrInputID, selectedExportacionesIndex, urlId]);

  // Helper function to open and uncollapse the generate tab
  const openGenerateTab = useCallback(() => {
    // Collapse sidebar if it's not already collapsed
    if (!isSidebarCollapsed && setIsSidebarCollapsed) {
      setIsSidebarCollapsed(true);
      localStorage.setItem('isSidebarCollapsed', JSON.stringify(true));
    }
    // Always open the tab when event is received
    setShowGenerateTab(true);
    // Uncollapse the tab when opening
    setLocalGenerateTabCollapsed(false);
    localStorage.setItem('isGenerateTabCollapsed', JSON.stringify(false));
    setIsGenerateTabOpen?.(true);
    setIsGenerateTabCollapsed?.(false);
  }, [isSidebarCollapsed, setIsSidebarCollapsed, setIsGenerateTabOpen, setIsGenerateTabCollapsed]);

  // Listen for generate tab open event - always active, even when tab is closed
  useEffect(() => {
    const handleOpenGenerateTab = () => {
      openGenerateTab();
      // Refresh JSON when tab opens to ensure it shows the latest selected row
      setEditedJsonString(getCurrentJsonString());
    };
    
    window.addEventListener('openGenerateTab', handleOpenGenerateTab);
    return () => {
      window.removeEventListener('openGenerateTab', handleOpenGenerateTab);
    };
  }, [openGenerateTab, getCurrentJsonString]);

  // Listen for generate Taric from tab button
  useEffect(() => {
    const handleGenerateTaricFromTab = () => {
      // Dispatch event that Dashboard can listen to
      window.dispatchEvent(new CustomEvent('executeGenerateTaric'));
    };

    window.addEventListener('generateTaricFromTab', handleGenerateTaricFromTab);
    return () => {
      window.removeEventListener('generateTaricFromTab', handleGenerateTaricFromTab);
    };
  }, []);

  // Listen for successful generation to close the tab
  useEffect(() => {
    const handleGenerationSuccess = () => {
      setShowGenerateTab(false);
      setIsGenerateTabOpen?.(false);
    };

    window.addEventListener('generationSuccess', handleGenerationSuccess);
    return () => {
      window.removeEventListener('generationSuccess', handleGenerationSuccess);
    };
  }, [setIsGenerateTabOpen]);

  // Sync parent state when local state changes
  useEffect(() => {
    setIsGenerateTabOpen?.(showGenerateTab);
  }, [showGenerateTab, setIsGenerateTabOpen]);

  useEffect(() => {
    setIsGenerateTabCollapsed?.(isGenerateTabCollapsed);
  }, [isGenerateTabCollapsed, setIsGenerateTabCollapsed]);

  // Cleanup: Close tab when component unmounts (e.g., navigating away from declaration route)
  useEffect(() => {
    return () => {
      // When component unmounts, ensure parent state is closed
      setIsGenerateTabOpen?.(false);
      setIsGenerateTabCollapsed?.(false);
    };
  }, [setIsGenerateTabOpen, setIsGenerateTabCollapsed]);

  if (!showGenerateTab) {
    return null;
  }

  const getCurrentExportacionIdAndTaric = () => {
    // Priority 1: Try to get ID from URL (when in DeclarationView)
    if (urlId) {
      const declarationId = typeof urlId === 'string' ? parseInt(urlId, 10) : urlId;
      // Find the declaration in groupedTaricJsonOutputs (export or import)
      for (const group of groupedTaricJsonOutputs) {
        const exportacion = findGroupEntryById(group, declarationId);
        if (exportacion && exportacion.taric) {
          return {
            id: exportacion.id,
            taricJson: exportacion.taric
          };
        }
      }
      // If not found in grouped outputs, try selectedDeclaration
      if (selectedDeclaration && selectedDeclaration.id === declarationId) {
        // Try to find taric from grouped outputs using selectedDeclaration
        for (const group of groupedTaricJsonOutputs) {
          const exportacion = findGroupEntryById(group, declarationId);
          if (exportacion && exportacion.taric) {
            return {
              id: exportacion.id,
              taricJson: exportacion.taric
            };
          }
        }
      }
    }

    // Priority 2: Try to extract from preparedJson Find the declaration that matches the taric data from preparedJson
    const taricFromPrepared = preparedJson?.taricJson;
    if (taricFromPrepared) {
      // Try to find the declaration by matching the taric data (export or import)
      for (const group of groupedTaricJsonOutputs) {
        const matchesTaric = (exp) =>
          exp.taric && JSON.stringify(exp.taric) === JSON.stringify(taricFromPrepared);
        const exportacion = (group.exportaciones || []).find(matchesTaric)
          || (group.importaciones || []).find(matchesTaric);
        if (exportacion) {
          return {
            id: exportacion.id, // Use the actual declaration ID, not the internal duas[0].id
            taricJson: exportacion.taric
          };
        }
      }
      // If not found by matching, try using ocrInputID and selectedExportacionesIndex
      if (ocrInputID && selectedExportacionesIndex !== null) {
        const matchingGroup = groupedTaricJsonOutputs.find(
          group => String(group.ocr_id) === String(ocrInputID)
        );
        const selectedExportacion = groupEntryAtIndex(matchingGroup, selectedExportacionesIndex);
        if (selectedExportacion) {
          return {
            id: selectedExportacion.id,
            taricJson: selectedExportacion.taric || taricFromPrepared
          };
        }
      }
    }

    // Priority 3: Use selectedDeclaration ID if available
    if (selectedDeclaration?.id) {
      const declarationId = selectedDeclaration.id;
      // Find the declaration in groupedTaricJsonOutputs (export or import)
      for (const group of groupedTaricJsonOutputs) {
        const exportacion = findGroupEntryById(group, declarationId);
        if (exportacion && exportacion.taric) {
          return {
            id: exportacion.id,
            taricJson: exportacion.taric
          };
        }
      }
    }

    // Priority 4: Fallback to groupedTaricJsonOutputs using ocrInputID and selectedExportacionesIndex
    if (ocrInputID && selectedExportacionesIndex !== null && groupedTaricJsonOutputs.length > 0) {
      const matchingGroup = groupedTaricJsonOutputs.find(
        group => String(group.ocr_id) === String(ocrInputID) ||
                 group.ocr_id === parseInt(ocrInputID)
      );

      const selectedExportacion = groupEntryAtIndex(matchingGroup, selectedExportacionesIndex);
      if (selectedExportacion) {
        // New structure: use taric object directly
        const taricJson = selectedExportacion.taric || {};
        return {
          id: selectedExportacion.id,
          taricJson
        };
      }
    }
    
    return { id: null, taricJson: null };
  };

  const handleResend = async () => {
    try {
      setHasError(false);
      setErrorMessage('');
      setErrorDetails([]);
      setIsResending(true);

      const { id } = getCurrentExportacionIdAndTaric();
      if (!id) {
        setHasError(true);
        setErrorMessage('Missing declaration ID to resend.');
        setShowSuccessModal(true);
        setIsResending(false);
        return;
      }

      // Parse the edited JSON string, fallback to original if parsing fails
      let taricToSend = {};
      try {
        taricToSend = JSON.parse(editedJsonString);
      } catch (parseError) {
        setHasError(true);
        setErrorMessage('Invalid JSON format. Please check your edits.');
        setShowSuccessModal(true);
        setIsResending(false);
        return;
      }

      // Determine the correct format to send The API expects: { ocr_input_id: "...", taric: {...} } This matches the format from GENERATE_TARIC_JSON reducer and initial generation
      let bodyToSend;
      if (taricToSend.ocr_input_id && taricToSend.taric) {
        // Already in correct format: { ocr_input_id: "...", taric: {...} } Send as-is (same format as initial generation in useDocumentProcessing.js)
        bodyToSend = taricToSend;
      } else if (taricToSend.taric && !taricToSend.ocr_input_id) {
        // Has taric property but no ocr_input_id - add ocr_input_id
        bodyToSend = {
          ocr_input_id: ocrInputID || "",
          taric: taricToSend.taric
        };
      } else if (taricToSend.clientes || taricToSend.agentes || taricToSend.remitentes || taricToSend.duas) {
        // Just the taric object (clientes, agentes, duas, etc.) - wrap it with ocr_input_id This happens when JSON comes from grouped outputs (selectedExportacion.taric)
        bodyToSend = {
          ocr_input_id: ocrInputID || "",
          taric: taricToSend
        };
      } else {
        // Fallback: try to construct from what we have
        bodyToSend = {
          ocr_input_id: ocrInputID || taricToSend.ocr_input_id || "",
          taric: taricToSend.taric || taricToSend
        };
      }

      // Normalize binary fields (e.g. tiene_contenedores) to scalar 0/1 so API never receives array [0]/[1]
      bodyToSend = ensureTaricJsonBinaryFields(bodyToSend);

      // Use the same route as initial generation in useDocumentProcessing.js
      const url = `${process.env.REACT_APP_API_BASE_URL}/api/items/taric_json_output/`;
      

      const response = await apiFetch(url, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(bodyToSend)
      });

      const responseText = await response.text().catch(() => '');
      let responseData = null;
      
      try {
        responseData = JSON.parse(responseText);
        // CRITICAL: Validate that responseData is an object (not null, not primitive)
        if (responseData !== null && typeof responseData !== 'object') {
          throw new Error('Response is not a valid object');
        }
      } catch (parseError) {
        // If response is not JSON or invalid, handle as error
        setHasError(true);
        setErrorMessage(responseText || `Request failed with status ${response.status}`);
        setErrorDetails([]);
        dispatch(setErrorExpedientes([]));
        dispatch(setSuccessfulExpedientes([]));
        setShowSuccessModal(true);
        setIsResending(false);
        return;
      }

      // CRITICAL: Handle null/undefined responseData after parsing
      if (!responseData || typeof responseData !== 'object') {
        setHasError(true);
        setErrorMessage("Invalid response from server. Please try again.");
        setErrorDetails([]);
        dispatch(setErrorExpedientes([]));
        dispatch(setSuccessfulExpedientes([]));
        setShowSuccessModal(true);
        setIsResending(false);
        return;
      }

      // OPTIMIZED: Extract and store ocr_input_id from API response per workflow
      if (responseData && responseData.ocr_input_id && activeWorkflowSessionId) {
        const ocrInputIdStr = String(responseData.ocr_input_id).trim();
        const hasChanged = ocrInputIdStr !== storedOcrInputIdRef.current || 
                          activeWorkflowSessionId !== storedWorkflowSessionIdRef.current;
        
        if (ocrInputIdStr && hasChanged) {
          // Batch updates: dispatch both in sequence (Redux batches automatically)
          dispatch(setOcrInputID(ocrInputIdStr));
          dispatch(updateWorkflowSessionState(activeWorkflowSessionId, {
            ocrInputID: ocrInputIdStr,
          }));
          
          // Update refs to prevent duplicate dispatches
          storedOcrInputIdRef.current = ocrInputIdStr;
          storedWorkflowSessionIdRef.current = activeWorkflowSessionId;
        }
      }

      if (!response.ok) {
        setHasError(true);
        
        // Handle error response with success/error arrays (same format as initial generation)
        if (responseData && Array.isArray(responseData.errors)) {
          const errors = responseData.errors;
          const success = Array.isArray(responseData.success) ? responseData.success : [];
          
          // Extract error expedientes and store in Redux
          const errorExpedientes = errors.map(item => 
            item.expediente || `#${(readRowIndex(item) || 0) + 1}`
          );
          dispatch(setErrorExpedientes(errorExpedientes));
          
          // Extract successful expedientes if any
          if (success.length > 0) {
            const successfulExpedientes = success.map(item => 
              item.expediente || `#${(readRowIndex(item) || 0) + 1}`
            );
            dispatch(setSuccessfulExpedientes(successfulExpedientes));
          } else {
            dispatch(setSuccessfulExpedientes([]));
          }
          
          // CRITICAL: Filter declarations - remove successful ones, keep only errors
          const errorIndices = errors
            .map(err => readRowIndex(err))
            .filter(idx => typeof idx === 'number' && idx >= 0);
          
          if (errorIndices.length > 0) {
            dispatch(filterDeclarationsByErrors(errorIndices));
          }
          
          // Extract just the message for the modal
          setErrorMessage(responseData.message || 'Failed to resend Taric JSON output');
          
          // Parse errors object into a flat list for display
          const errorsList = [];
          responseData.errors.forEach(error => {
            if (error.errors && typeof error.errors === 'object') {
              const flattenErrors = (obj, prefix = '') => {
                Object.keys(obj).forEach(key => {
                  const value = obj[key];
                  const fullKey = prefix ? `${prefix}.${key}` : key;
                  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                    flattenErrors(value, fullKey);
                  } else {
                    errorsList.push({ field: fullKey, message: value });
                  }
                });
              };
              flattenErrors(error.errors);
            }
          });
          setErrorDetails(errorsList);
        } else if (responseData) {
          // Legacy error format - single error object
          setErrorMessage(responseData.message || 'Failed to resend Taric JSON output');
          
          // Parse errors object into a flat list
          const errorsList = [];
          if (responseData.errors) {
            const flattenErrors = (obj, prefix = '') => {
              Object.keys(obj).forEach(key => {
                const value = obj[key];
                const fullKey = prefix ? `${prefix}.${key}` : key;
                if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                  flattenErrors(value, fullKey);
                } else {
                  errorsList.push({ field: fullKey, message: value });
                }
              });
            };
            flattenErrors(responseData.errors);
          }
          setErrorDetails(errorsList);
          dispatch(setErrorExpedientes([]));
          dispatch(setSuccessfulExpedientes([]));
        } else {
          setErrorMessage(responseText || `Request failed with status ${response.status}`);
          setErrorDetails([]);
          dispatch(setErrorExpedientes([]));
          dispatch(setSuccessfulExpedientes([]));
        }
      } else {
        // Success response
        setHasError(false);
        setErrorDetails([]);
        
        // Handle success response with success/error arrays (same format as initial generation)
        if (responseData && Array.isArray(responseData.success)) {
          // Extract successful expedientes and store in Redux
          const successfulExpedientes = responseData.success.map(item => 
            item.expediente || `#${(readRowIndex(item) || 0) + 1}`
          );
          dispatch(setSuccessfulExpedientes(successfulExpedientes));
          
          // Extract error expedientes if any
          const errors = Array.isArray(responseData.errors) ? responseData.errors : [];
          if (errors.length > 0) {
            const errorExpedientes = errors.map(item => 
              item.expediente || `#${(readRowIndex(item) || 0) + 1}`
            );
            dispatch(setErrorExpedientes(errorExpedientes));
            setHasError(true);
            setErrorMessage(responseData.message || 'Some declarations have errors');
            
            // CRITICAL: Filter declarations - remove successful ones, keep only errors
            const errorIndices = errors
              .map(err => readRowIndex(err))
              .filter(idx => typeof idx === 'number' && idx >= 0);
            
            if (errorIndices.length > 0) {
              dispatch(filterDeclarationsByErrors(errorIndices));
            }
          } else {
            dispatch(setErrorExpedientes([]));
          }
          
          // CRITICAL: If no expedientes were created (neither success nor errors), treat as error This prevents showing success modal when nothing was actually generated
          if (successfulExpedientes.length === 0 && errors.length === 0) {
            setHasError(true);
            setErrorMessage("No declarations were generated. Please check your data and try again.");
            dispatch(setErrorExpedientes([]));
            dispatch(setSuccessfulExpedientes([]));
          }
        } else {
          // Legacy success - no arrays, just success CRITICAL: If legacy format with no expedientes, treat as error
          dispatch(setSuccessfulExpedientes([]));
          dispatch(setErrorExpedientes([]));
          setHasError(true);
          setErrorMessage("No declarations were generated. Please check your data and try again.");
        }
      }

      // Show modal for both success and failure, icon/text differs
      setShowSuccessModal(true);
    } catch (err) {
      setHasError(true);
      setErrorMessage(err?.message || 'Unexpected error while resending.');
      setErrorDetails([]);
      setShowSuccessModal(true);
    } finally {
      setIsResending(false);
    }
  };

  // Handle error field click to scroll to field in JSON textarea
  const handleErrorFieldClick = (error) => {
    if (!textareaRef.current || !error.field) return;

    const fieldName = error.field.split('.').pop();
    const searchKey = `"${fieldName}"`;
    const jsonString = editedJsonString;
    
    // Find all occurrences of the field name
    let bestMatch = -1;
    
    // Try to find the most relevant match by looking for the field in context
    const fieldPath = error.field.split('.');
    
    // First, try to find the field with its path context
    if (fieldPath.length > 1) {
      // Look for the parent path + field name
      const parentPath = fieldPath.slice(0, -1);
      const parentKey = `"${parentPath[parentPath.length - 1]}"`;
      const parentIndex = jsonString.indexOf(parentKey);
      
      if (parentIndex !== -1) {
        // Search for the field after the parent
        const searchAfterParent = jsonString.substring(parentIndex);
        const relativeIndex = searchAfterParent.indexOf(searchKey);
        if (relativeIndex !== -1) {
          bestMatch = parentIndex + relativeIndex;
        }
      }
    }
    
    // If no contextual match, just find the first occurrence
    if (bestMatch === -1) {
      bestMatch = jsonString.indexOf(searchKey);
    }
    
    if (bestMatch !== -1) {
      textareaRef.current.focus();
      
      // Set selection to highlight the field
      const fieldEnd = bestMatch + searchKey.length;
      textareaRef.current.setSelectionRange(bestMatch, fieldEnd);
      
      // Calculate scroll position to position field at 30% from top of textarea container
      const textBeforeMatch = jsonString.substring(0, bestMatch);
      const linesBefore = (textBeforeMatch.match(/\n/g) || []).length;
      
      // Get line height from computed style
      const computedStyle = window.getComputedStyle(textareaRef.current);
      const lineHeight = parseFloat(computedStyle.lineHeight) || 18;
      
      // Calculate the position of the field in pixels from the top of the textarea content
      const fieldPosition = linesBefore * lineHeight;
      
      // Get the visible height of the textarea (container viewport)
      const containerHeight = textareaRef.current.clientHeight;
      
      // Calculate scroll position to place field at 30% from top of the container 30% means: field should be at 30% down from the top of the visible container
      const scrollPosition = fieldPosition - (containerHeight * 0.05);
      
      // Scroll to position (ensure it's not negative)
      textareaRef.current.scrollTop = Math.max(0, scrollPosition);
      
      // Keep selection visible
      setTimeout(() => {
        textareaRef.current.setSelectionRange(bestMatch, fieldEnd);
      }, 50);
    }
  };

  // Derive whether the JSON currently in the editor is an import or export so
  // the submit button reads correctly. The editor value may be either the full
  // { ocr_input_id, taric } envelope or a bare taric object.
  let currentSubmitKind = 'export';
  try {
    const parsed = JSON.parse(editedJsonString);
    const taricObj = parsed?.taric || parsed;
    currentSubmitKind = readDuasDeclarations(taricObj).kind;
  } catch {
    currentSubmitKind = 'export';
  }

  return (
    <div className={`min-h-screen max-h-screen overflow-y-auto p-4 pt-10 pl-2 flex gap-4 text-left flex-col transition-all duration-200 ${isGenerateTabCollapsed ? 'w-20' : 'w-80'}`}>
      {/* Header */}
      <div className="min-h-[40px] flex gap-0 items-center overflow-x-auto overflow-y-hidden rounded-full">
        {!isGenerateTabCollapsed && (
          <>
            <h2 className="text-lg font-semibold flex-1 pl-2">
              {getDeclarationManagementTranslation('jsonFile', isSpanish)}
            </h2>
            <button
              onClick={() => {
                setShowGenerateTab(false);
                setLocalGenerateTabCollapsed(false);
                localStorage.setItem('isGenerateTabCollapsed', JSON.stringify(false));
                setIsGenerateTabOpen?.(false);
                setIsGenerateTabCollapsed?.(false);
              }}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
              title={getDeclarationManagementTranslation('close', isSpanish)}
            >
              <X className="size-5" />
            </button>
          </>
        )}
      </div>

      {!isGenerateTabCollapsed && (
        <>
          {/* Error List - Show before JSON container if there are errors */}
          {errorDetails.length > 0 && (
            <div className="mb-4">
              <h2 className="font-semibold text mb-2 text-red-600">
                {isSpanish ? "Errores de validación" : "Validation Errors"}
              </h2>
              <div className="text-sm text-red-500 space-y-2">
                {errorDetails.map((error, index) => (
                  <div
                    key={index}
                    className="p-2 rounded text-sm break-words bg-red-100 cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={() => handleErrorFieldClick(error)}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold break-words text-gray-700">
                        {error.message}
                      </div>
                      <div className="text-[10px] text-gray-500 font-mono break-all">
                        {error.field}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* JSON Container - editable textarea */}
          <div className="flex-1 bg-gray-100 rounded-lg" style={{ minHeight: 0 }}>
            <textarea
              ref={textareaRef}
              value={editedJsonString}
              onChange={(e) => setEditedJsonString(e.target.value)}
              className="w-full h-full p-4 text-xs font-mono bg-transparent border-none resize-none focus:outline-none focus:ring-0"
              style={{ 
                minHeight: '200px',
                fontFamily: 'monospace',
                lineHeight: '1.5'
              }}
              spellCheck={false}
            />
          </div>

          {/* Generate Button at Bottom */}
          <div className="">
            <button
              className="flex items-center gap-2 py-2 px-5 rounded-full bg-default border disabled:opacity-50 disabled:cursor-not-allowed w-full justify-center"
              onClick={handleResend}
              disabled={isResending}
            >
              <FileJson className="size-5 stroke-white"/>
              <span className='text-white text-sm'>
                {isResending
                  ? (isSpanish ? 'Enviando...' : 'Sending...')
                  : getDeclarationManagementTranslation(currentSubmitKind === 'import' ? 'importToTaric' : 'exportToTaric', isSpanish)}
              </span>
            </button>
          </div>
        </>
      )}
      {showSuccessModal && (
        <GeneratedSuccessfullyModal
          setIsSuccessfulModalOpen={setShowSuccessModal}
          hasError={hasError}
          errorMessage={errorMessage}
        />
      )}
    </div>
  );
};

